import { FC, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import { HrailTower } from "../../../../../http/hrail/types/towers";
import { useHrailTowersApi } from "../../../../../http/hrail/useTowers";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface TowerFormBody {
  item: SelectOption | null;
  heightMm: number | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

type SelectOption = {
  id: string;
  label: string;
};

interface TowerFormProps {
  tower?: HrailTower;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const TowerForm: FC<TowerFormProps> = ({ tower, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const towerApi = useHrailTowersApi();
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SelectOption[]>([]);
  const itemAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, (query, signal) =>
    itemsApi.getAdminItems(query, signal).then((items) => items.map(toItemOption)),
  );

  const defaultValues: TowerFormBody = {
    item: tower?.item ? toItemOption(tower.item) : null,
    heightMm: tower?.heightMm ?? 0,
    order: tower?.order ?? 50,
    visibility: tower?.visibility ?? "internal",
  };

  useEffect(() => {
    loadItemOptions("").then(setItemOptions).catch(() => setItemOptions([]));
  }, []);

  const onSubmit = () => async (formData: TowerFormBody) => {
    setIsSubmitting(true);

    try {
      if (tower) {
        await towerApi.updateAdminHrailTower(tower.item.id, {
  heightMm: formData.heightMm ?? 0,
  order: formData.order ?? 50,
  visibility: formData.visibility,
});
        dispatch(showSuccessNotification(i18n.translation));
        onSaved?.();
        onClose?.();
      } else {
        const res = await towerApi.insertAdminHrailTower({
  itemId: formData.item?.id ?? "",
  heightMm: formData.heightMm ?? 0,
  order: formData.order ?? 50,
  visibility: formData.visibility,
});
        dispatch(showSuccessNotification(i18n.translation));
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<TowerFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [tower]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <CustomAsyncSelect<SelectOption>
                      {...field}
                      inputId={labelId}
                      defaultOptions={itemOptions}
                      loadOptions={loadItemOptions}
                      isClearable={false}
                      isDisabled={isDisabled || isLoading || isSubmitting || !!tower}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      isInvalid={isInvalid}
                      onChange={(value) => setValue("item", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="heightMm"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.height} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("heightMm", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

type ItemOptionSource = { id: string; code?: string | null; description?: string | null; name?: string | null };

const toItemOption = (item: ItemOptionSource): SelectOption => ({ id: item.id, label: itemLabel(item) });

export default TowerForm;
